import {Injectable} from '@angular/core';
import {HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Observable} from 'rxjs';
import {finalize, shareReplay} from 'rxjs/operators';

/** Set this context token to true to force a request past the deduplication. */
export const SKIP_REQUEST_DEDUP = new HttpContextToken<boolean>(() => false);

/**
 * Fasst identische GET-Requests zusammen, die kurz hintereinander abgehen.
 *
 * Hintergrund (gemessen 2026-07-30): beim Öffnen eines Projekts feuerte das Frontend innerhalb von
 * sechs Sekunden 8× `/auth/profile`, 7× `/projects/{id}`, 3× `/projects/{id}/files` und je 3×
 * `/state/{id}`, `/workflows/pins`, `/users/preferences/{id}` — jeweils identische Anfragen, jede
 * 300–2200 ms teuer. Ursache sind Route-Resolver, die bei jeder Aktivierung neu laden
 * (`UserResolver` ohne jeden Schutz, `ProjectResolver` mit einem Kurzschluss, der bei
 * aufeinanderfolgenden Aktivierungen nicht greift), plus zehn Komponenten, die `profile()` zusätzlich
 * selbst aufrufen.
 *
 * Zwei Effekte, absichtlich getrennt:
 * <ul>
 *   <li><b>Laufende Anfrage</b>: ein zweiter identischer GET bekommt die Antwort der laufenden. Hier gibt
 *       es keinerlei Aktualitätsrisiko -- eine gleichzeitige zweite Anfrage hätte dasselbe geliefert.</li>
 *   <li><b>Kurzes Nachspielfenster</b>: nach Abschluss wird die Antwort noch {@link REPLAY_WINDOW_MS}
 *       nachgespielt. Das fängt die beobachteten Ketten ab, bei denen die nächste Anfrage unmittelbar
 *       nach Abschluss der vorigen startet. Das Fenster ist bewusst klein: es entfernt Duplikate,
 *       ersetzt aber keinen Cache und hält keine Daten über einen Benutzerklick hinweg.</li>
 * </ul>
 *
 * Bewusst nur GET, und nur ohne {@code reportProgress} (Download-Fortschritt gehört nicht geteilt).
 * SSE ist nicht betroffen, das läuft über `fetch()` (siehe `SseService`), nicht über HttpClient.
 * Einzelne Aufrufe können mit {@link SKIP_REQUEST_DEDUP} ausgenommen werden.
 *
 * Muss vor Ladeindikator und Fehlerbehandlung registriert sein (nur der `AuthInterceptor` steht
 * davor): dann durchlaufen zusammengefasste Anfragen die inneren Interceptor nicht mehr, der
 * Ladeindikator zählt also einmal statt achtmal und ein Fehler erzeugt eine Meldung statt acht.
 */
@Injectable()
export class RequestDedupInterceptor implements HttpInterceptor {

  /** Nachspielfenster nach Abschluss. Klein halten -- das ist eine Duplikatsperre, kein Cache. */
  private static readonly REPLAY_WINDOW_MS = 1000;

  private readonly shared = new Map<string, Observable<HttpEvent<unknown>>>();

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.method !== 'GET') {
      // Jede schreibende Anfrage verwirft das Fenster -- sonst würde ein Neuladen direkt nach einem
      // Upload/Umbenennen/Löschen (wie in files.component#loadFiles) den Stand VOR der Änderung
      // nachgespielt bekommen. Sowohl beim Absenden als auch beim Abschluss: ein GET, der während der
      // Änderung läuft, darf das Fenster nicht mit veralteten Daten neu füllen.
      this.shared.clear();
      return next.handle(request).pipe(finalize(() => this.shared.clear()));
    }

    if (request.reportProgress || request.context.get(SKIP_REQUEST_DEDUP)) {
      return next.handle(request);
    }

    // Das Token gehört zum Schlüssel: Wiederholt der AuthInterceptor einen GET nach einem 401 mit
    // erneuertem Token, darf er nicht auf den gescheiterten Request treffen. shareReplay setzt sich
    // nach einem Fehler zurück und würde beim nächsten Abonnenten den ALTEN Request (altes Token)
    // erneut absenden.
    const key = `${request.headers.get('Authorization') ?? ''} ${request.urlWithParams}`;
    const running = this.shared.get(key);
    if (running) {
      return running;
    }

    // finalize VOR shareReplay, damit es einmal beim Abschluss der Quelle läuft und nicht je Abonnent.
    // refCount: false, damit die Antwort auch dann nachgespielt wird, wenn der erste Aufrufer sich
    // bereits abgemeldet hat.
    const response = next.handle(request).pipe(
      finalize(() => setTimeout(
        () => this.shared.delete(key),
        RequestDedupInterceptor.REPLAY_WINDOW_MS,
      )),
      shareReplay({bufferSize: 1, refCount: false}),
    );

    this.shared.set(key, response);
    return response;
  }
}
